import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  DialogContentText,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Checkbox,
  Grid,
  CircularProgress,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useExperimentStore } from '../../store/experimentStore';
import { useRaspiStore } from '../../store/raspiStore';
import { useCameraStore } from '../../store/cameraStore';
import Plot from 'react-plotly.js';

interface ExperimentEditDialogProps {
  open: boolean;
  onClose: () => void;
  initialExpId: string | null;
  initiatingRaspiId: string | null;
}

const ExperimentEditDialog: React.FC<ExperimentEditDialogProps> = ({
  open,
  onClose,
  initialExpId,
  initiatingRaspiId
}) => {
  // ============================================================================
  // STORE HOOKS & REFS
  // ============================================================================
  const { 
    allExperiments, 
    fetchAllExperiments, 
    updateExperimentFromUser, 
    createExperiment,
    isLoading
  } = useExperimentStore();
  const { allRaspis, fetchAllRaspis } = useRaspiStore();
  const { allCameras, fetchAllCameras } = useCameraStore();

  // Create stable references for fetch functions to prevent infinite re-renders
  const fetchAllExperimentsRef = useRef(fetchAllExperiments);
  const fetchAllRaspisRef = useRef(fetchAllRaspis);
  const fetchAllCamerasRef = useRef(fetchAllCameras);

  // ============================================================================
  // SECTION 1: CORE MODE MANAGEMENT (NEW, EXP_VIEW, EXP_EDIT)
  // ============================================================================
  
  // State Variables
  const [selectedExpId, setSelectedExpId] = useState<string | null>(initialExpId);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    test_id: '',
    profile_id: '',
    profile_file_path: '',
    num_profiles: 0,
    raspi_id: '',
    chamber_id: '',
    chamber_enabled: true,
    cameras_enabled: [],
    start_time: null,
    end_time: null,
    exp_notes: '',
    pressure_steps: [],
    temperature_steps: [],
    time_steps: [],
    measurement_intervals: 10
  });
  
  // Refs for mode management
  const formDataInitializedRef = React.useRef<boolean>(false);
  const prevSelectedExpIdRef = React.useRef<string | null>(null);

  // Form data type
  type FormData = {
    test_id: string;
    profile_id: string;
    profile_file_path: string;
    num_profiles: number;
    raspi_id: string;
    chamber_id: string;
    chamber_enabled: boolean;
    cameras_enabled: Array<{
      camera_id: string;
      camera_gauge_id: string;
      camera_led_id: string;
      enabled: boolean;
    }>;
    start_time: string | null;
    end_time: string | null;
    exp_notes: string;
    pressure_steps: number[];
    temperature_steps: number[];
    time_steps: number[];
    measurement_intervals: number;
  };

  // Helper: Get default form data for NEW experiments
  const getDefaultFormData = (): FormData => ({
    test_id: '',
    profile_id: '',
    profile_file_path: '',
    num_profiles: 0,
    raspi_id: '',
    chamber_id: '',
    chamber_enabled: false,
    cameras_enabled: [],
    start_time: null,
    end_time: null,
    exp_notes: '',
    pressure_steps: [],
    temperature_steps: [],
    time_steps: [],
    measurement_intervals: 10
  });

  // Helper: Convert experiment from store to formData format (for EXP_EDIT)
  const experimentToFormData = (experiment: any): FormData => ({
    test_id: experiment.test_id ?? '',
    profile_id: experiment.profile_id ?? '',
    profile_file_path: experiment.profile_file_path ?? '',
    num_profiles: experiment.num_profiles ?? 0,
    raspi_id: experiment.raspi_id ?? '',
    chamber_id: experiment.chamber_id ?? '',
    chamber_enabled: experiment.chamber_enabled ?? false,
    cameras_enabled: experiment.cameras_enabled ?? [],
    start_time: experiment.start_time?.toISOString() ?? null,
    end_time: experiment.end_time?.toISOString() ?? null,
    exp_notes: experiment.exp_notes ?? '',
    pressure_steps: experiment.pressure_steps ?? [],
    temperature_steps: experiment.temperature_steps ?? [],
    time_steps: experiment.time_steps ?? [],
    measurement_intervals: experiment.measurement_intervals ?? 10
  });

  // Get display data: from store in VIEW mode, from formData in EDIT/NEW mode
  const getDisplayData = (): FormData => {
    if (selectedExpId === "__NEW__" || isEditMode) {
      // NEW or EXP_EDIT mode: use formData
      return formData;
    } else if (selectedExpId && allExperiments[selectedExpId]) {
      // EXP_VIEW mode: use store data
      const experiment = allExperiments[selectedExpId];
      return {
        test_id: experiment.test_id ?? '',
        profile_id: experiment.profile_id ?? '',
        profile_file_path: experiment.profile_file_path ?? '',
        num_profiles: experiment.num_profiles ?? 0,
        raspi_id: experiment.raspi_id ?? '',
        chamber_id: experiment.chamber_id ?? '',
        chamber_enabled: experiment.chamber_enabled ?? false,
        cameras_enabled: experiment.cameras_enabled ?? [],
        start_time: experiment.start_time?.toISOString() ?? null,
        end_time: experiment.end_time?.toISOString() ?? null,
        exp_notes: experiment.exp_notes ?? '',
        pressure_steps: experiment.pressure_steps ?? [],
        temperature_steps: experiment.temperature_steps ?? [],
        time_steps: experiment.time_steps ?? [],
        measurement_intervals: experiment.measurement_intervals ?? 10
      };
    }
    return formData; // Fallback
  };

  // Handle toggle: EXP_VIEW → EXP_EDIT
  const handleToggleEdit = () => {
    if (selectedExpId && selectedExpId !== "__NEW__" && allExperiments[selectedExpId]) {
      const experiment = allExperiments[selectedExpId];
      setFormData(experimentToFormData(experiment));
      
      if (experiment.test_id) {
        validateTestId(experiment.test_id);
      }
      
      setIsEditMode(true);
      setHasUnsavedChanges(false);
      formDataInitializedRef.current = false;
    }
  };

  // Handle toggle: EXP_EDIT → EXP_VIEW
  const handleToggleView = () => {
    if (selectedExpId && selectedExpId !== "__NEW__" && allExperiments[selectedExpId]) {
      // Discard formData changes and switch to VIEW mode
      setIsEditMode(false);
      setHasUnsavedChanges(false);
      setTestIdWarning(null);
      setProfileFileWarning(null);
      // Don't reset formData - it will be ignored in VIEW mode (getDisplayData uses store)
    }
  };

  // Mode initialization: Dialog open/close reset
  useEffect(() => {
    if (open) {
      fetchAllExperimentsRef.current();
      fetchAllRaspisRef.current();
      fetchAllCamerasRef.current();
      
      // Clear save error when dialog opens
      setSaveError(null);
      
      // Initialize edit mode based on initialExpId
      if (initialExpId === "__NEW__") {
        setIsEditMode(true);
      } else {
        setIsEditMode(false);
      }
      
      // Reset formData initialization flag when dialog opens
      formDataInitializedRef.current = false;
      prevSelectedExpIdRef.current = null;
    } else {
      // Reset when dialog closes
      formDataInitializedRef.current = false;
      prevSelectedExpIdRef.current = null;
    }
  }, [open, initialExpId]);

  // Mode initialization: Initial selectedExpId setup
  useEffect(() => {
    if (open && initiatingRaspiId && !selectedExpId) {
      // Only initialize if selectedExpId is not already set (prevents reset on store updates)
      let expId = initialExpId;
      let isNew = false;
      
      // Check if "__NEW__" placeholder (new experiment)
      if (expId === "__NEW__") {
        isNew = true;
        expId = "__NEW__";
      } else if (expId) {
        // Existing experiment - check if it exists in store
        isNew = !allExperiments[expId];
      } else {
        // If no exp_id provided, check if current RASPI has an experiment
        const currentRaspi = allRaspis[initiatingRaspiId];
        expId = currentRaspi?.exp_id || null;
        isNew = !expId;
      }
      
      setSelectedExpId(expId);
      setHasUnsavedChanges(isNew);
    }
  }, [open, initialExpId, initiatingRaspiId, selectedExpId, allExperiments, allRaspis]);

  // Mode initialization: Handle selectedExpId changes (NEW/VIEW/EDIT mode setup)
  useEffect(() => {
    if (!open) return;
    
    // Only process if selectedExpId actually changed (not just store update)
    const selectedExpIdChanged = selectedExpId !== prevSelectedExpIdRef.current;
    if (!selectedExpIdChanged && prevSelectedExpIdRef.current !== null) {
      // selectedExpId hasn't changed, just store updated - don't reset mode
      return;
    }
    
    // Store previous value BEFORE updating ref (needed to detect switch from "__NEW__")
    const previousExpId = prevSelectedExpIdRef.current;
    
    // Update ref to track current selectedExpId
    prevSelectedExpIdRef.current = selectedExpId;
    
    if (selectedExpId === "__NEW__") {
      // NEW experiment: Always EDIT mode
      setIsEditMode(true);
      
      // Initialize formData with defaults only if not already initialized
      if (!formDataInitializedRef.current) {
        const selectedRaspi = allRaspis[initiatingRaspiId || ''];
        const chamberId = selectedRaspi?.chamber_id || '';
        const cameraIds = selectedRaspi?.camera_ids || [];
        const hasChamber = !!(chamberId && chamberId.trim() !== '');
        const chamberEnabled = hasChamber && selectedRaspi?.raspi_status !== 'DISCONNECTED';
        const cameras_enabled = selectedRaspi?.raspi_status !== 'DISCONNECTED' ? cameraIds.map(cameraId => ({
          camera_id: cameraId,
          camera_gauge_id: allCameras[cameraId]?.camera_gauge_id ?? '',
          camera_led_id: allCameras[cameraId]?.camera_led_id ?? '',
          enabled: true
        })) : [];

        setFormData({
          ...getDefaultFormData(),
          num_profiles: 1,
          raspi_id: initiatingRaspiId ?? '',
          chamber_id: chamberId ?? '',
          chamber_enabled: chamberEnabled ?? false,
          cameras_enabled: cameras_enabled ?? []
        });
        
        setHasUnsavedChanges(true);
        formDataInitializedRef.current = true;
      }
      
      setTestIdWarning(null);
      setProfileFileWarning(null);
    } else if (selectedExpId && allExperiments[selectedExpId]) {
      // Existing experiment: VIEW mode by default (only if selectedExpId changed)
      // If switching from "__NEW__", always switch to VIEW (preserve NEW formData)
      // If already in VIEW mode for this exp_id, stay in VIEW
      // Only preserve EDIT mode if user explicitly toggled it for this same exp_id
      const wasNewExperiment = previousExpId === "__NEW__";
      if (wasNewExperiment || !isEditMode) {
        setIsEditMode(false);
      }
      // Don't load into formData - display from store in VIEW mode
      setHasUnsavedChanges(false);
      setTestIdWarning(null);
      setProfileFileWarning(null);
    }
  }, [selectedExpId, open, initiatingRaspiId, allExperiments, allRaspis, allCameras, isEditMode]);

  // ============================================================================
  // SECTION 2: FIELD DATA HANDLING
  // ============================================================================
  
  // State Variables
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Handle form field changes
  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
    
    // Clear save error when user makes changes
    setSaveError(null);
    
    // Add validation for test_id
    if (field === 'test_id') {
      validateTestId(value);
    }
  };

  // Handle profile file selection
  const handleProfileFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file extension
    if (!file.name.endsWith('.json')) {
      setProfileFileWarning('Please select a JSON file');
      return;
    }

    // Validate file name format (P + number + .json)
    const fileName = file.name.replace('.json', '');
    const profilePattern = /^P\d+$/;
    if (!profilePattern.test(fileName)) {
      setProfileFileWarning('Profile file should be named "P" followed by numbers (e.g., P001.json, P123.json)');
      return;
    }

    // Read and parse the JSON file content
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const fileContent = e.target?.result as string;
        const profileData = JSON.parse(fileContent);
        
        // Extract Profile_ID from file content (preferred) or use filename
        const profileId = profileData["Profile_ID"] || fileName;
        
        // Map actual profile file field names to system field names
        const pressureSteps = profileData["Pressure Steps [mmHg]"];
        const temperatureSteps = profileData["Temperature Steps [C]"];
        const timeSteps = profileData["Time Steps [sec]"];
        const measurementIntervals = profileData["Measurement Intervals"];
        
        // Validate required fields exist
        if (!pressureSteps || !temperatureSteps || !timeSteps) {
          setProfileFileWarning('Invalid profile file format. Must contain "Pressure Steps [mmHg]", "Temperature Steps [C]", and "Time Steps [sec]" arrays.');
          return;
        }
        
        // Validate arrays have the same length
        if (pressureSteps.length !== temperatureSteps.length || pressureSteps.length !== timeSteps.length) {
          setProfileFileWarning('Profile arrays must have the same length.');
          return;
        }
        
        // Validate at least one step exists
        if (pressureSteps.length === 0) {
          setProfileFileWarning('Profile file must contain at least one pressure step.');
          return;
        }
        
        // Update form data with profile content
        setFormData(prev => ({
          ...prev,
          profile_id: profileId,
          profile_file_path: file.name,
          pressure_steps: pressureSteps,
          temperature_steps: temperatureSteps,
          time_steps: timeSteps,
          measurement_intervals: measurementIntervals || prev.measurement_intervals || 10,
        }));
        
        // File path is stored in formData.profile_file_path
        setProfileFileWarning(null);
        setSaveError(null);
        setHasUnsavedChanges(true);
        
      } catch (error) {
        console.error('Error parsing profile file:', error);
        setProfileFileWarning('Error reading profile file. Please ensure it is a valid JSON file.');
      }
    };
    
    reader.onerror = () => {
      setProfileFileWarning('Error reading file. Please try again.');
    };
    
    // Start reading the file
    reader.readAsText(file);
  };

  // Trigger profile file dialog
  const handleProfileButtonClick = () => {
    profileFileInputRef.current?.click();
  };

  // File input reference for profile selection
  const profileFileInputRef = React.useRef<HTMLInputElement>(null);

  // ============================================================================
  // SECTION 3: DATA VALIDATION
  // ============================================================================
  
  // State Variables
  const [testIdWarning, setTestIdWarning] = useState<string | null>(null);
  const [profileFileWarning, setProfileFileWarning] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Validate Test ID format
  const validateTestId = (testId: string) => {
    if (!testId || testId === 'N/A') {
      setTestIdWarning(null);
      return;
    }
    
    // Check if it starts with "T" followed by numbers
    const testIdPattern = /^T\d+$/;
    if (!testIdPattern.test(testId)) {
      setTestIdWarning('Test ID should start with "T" followed by numbers (e.g., T001, T123)');
    } else {
      setTestIdWarning(null);
    }
  };

  // Comprehensive save validation - experiment fields and camera configurations
  const validateExperimentSave = (formData: any): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Validate basic experiment configuration fields
    if (!formData.test_id?.trim()) {
      errors.push('Test ID is required');
    }

    if (!formData.profile_id?.trim()) {
      errors.push('Profile ID is required');
    }

    if (formData.num_profiles <= 0) {
      errors.push('Number of profiles must be greater than 0');
    }

    // Validate camera configurations - prevent Alert 201/202
    if (formData.cameras_enabled && formData.cameras_enabled.length > 0) {
      const enabledCameras = formData.cameras_enabled.filter((camera: any) => camera.enabled);
      const gaugeIds = new Set<string>();

      enabledCameras.forEach((camera: any, index: number) => {
        const cameraIdentifier = camera.camera_id || `at position ${index + 1}`;

        // Check for missing or empty camera_gauge_id (prevents Alert 202)
        if (!camera.camera_gauge_id || !camera.camera_gauge_id.trim()) {
          errors.push(`Camera ${cameraIdentifier}: Gauge ID is required (cannot be empty)`);
        } else {
          // Check for duplicate gauge IDs
          if (gaugeIds.has(camera.camera_gauge_id.trim())) {
            errors.push(`Camera ${cameraIdentifier}: Duplicate gauge ID "${camera.camera_gauge_id}" (already used by another camera)`);
          } else {
            gaugeIds.add(camera.camera_gauge_id.trim());
          }
        }

        // Check for missing camera_id
        if (!camera.camera_id) {
          errors.push(`Camera at position ${index + 1}: Camera ID is missing`);
        }

        // LED ID validation removed - not required for experiment run
      });
    }

    return { isValid: errors.length === 0, errors };
  };

  // ============================================================================
  // SECTION 4: HARDWARE HELPERS
  // ============================================================================
  
  // Get cameras for selected RASPI
  const getSelectedRaspiCameras = () => {
    const raspiId = displayData.raspi_id;
    if (!raspiId || !allRaspis[raspiId]) return [];
    
    const raspi = allRaspis[raspiId];
    return (raspi.camera_ids || []).map(cameraId => ({
      id: cameraId,
      data: allCameras[cameraId] || null
    }));
  };

  // Helper functions to check hardware availability
  const hasChamberConnected = (): boolean => {
    const raspiId = displayData.raspi_id;
    if (!raspiId || !allRaspis[raspiId]) return false;
    const raspi = allRaspis[raspiId];
    // Check if RASPI has a chamber_id and RASPI is connected
    return !!(raspi.chamber_id && raspi.chamber_id.trim() !== '' && raspi.raspi_status !== 'DISCONNECTED');
  };

  const hasCamerasConnected = (): boolean => {
    const raspiId = displayData.raspi_id;
    if (!raspiId || !allRaspis[raspiId]) return false;
    const raspi = allRaspis[raspiId];
    // Check if RASPI has cameras and RASPI is connected
    return !!(raspi.camera_ids && raspi.camera_ids.length > 0 && raspi.raspi_status !== 'DISCONNECTED');
  };

  const getChamberStatus = (): { hasChamber: boolean; chamberId: string; isConnected: boolean } => {
    const raspiId = displayData.raspi_id;
    if (!raspiId || !allRaspis[raspiId]) {
      return { hasChamber: false, chamberId: '', isConnected: false };
    }
    
    const raspi = allRaspis[raspiId];
    const hasChamber = !!(raspi.chamber_id && raspi.chamber_id.trim() !== '');
    const chamberId = raspi.chamber_id || '';
    
    // Check if RASPI is connected (which means its hardware is available)
    const isConnected = raspi.raspi_status !== 'DISCONNECTED';
    
    return { hasChamber, chamberId, isConnected };
  };

  const getCameraStatus = (): { hasCameras: boolean; cameraCount: number; connectedCount: number } => {
    const raspiId = displayData.raspi_id;
    if (!raspiId || !allRaspis[raspiId]) {
      return { hasCameras: false, cameraCount: 0, connectedCount: 0 };
    }
    
    const raspi = allRaspis[raspiId];
    const cameraIds = raspi.camera_ids || [];
    const hasCameras = cameraIds.length > 0;
    
    // If RASPI is connected, all its cameras are considered connected
    const connectedCount = raspi.raspi_status !== 'DISCONNECTED' ? cameraIds.length : 0;
    
    return { hasCameras, cameraCount: cameraIds.length, connectedCount };
  };

  // Helper: Update hardware configuration when RASPI changes
  const updateHardwareForRaspi = (newRaspiId: string) => {
    if (!newRaspiId || !allRaspis[newRaspiId]) return;
    
    const selectedRaspi = allRaspis[newRaspiId];
    const chamberId = selectedRaspi.chamber_id || '';
    const cameraIds = selectedRaspi.camera_ids || [];
    const hasChamber = !!(chamberId && chamberId.trim() !== '');
    const chamberEnabled = hasChamber && selectedRaspi.raspi_status !== 'DISCONNECTED';
    const cameras_enabled = selectedRaspi.raspi_status !== 'DISCONNECTED' ? cameraIds.map(cameraId => ({
      camera_id: cameraId,
      camera_gauge_id: allCameras[cameraId]?.camera_gauge_id ?? '',
      camera_led_id: allCameras[cameraId]?.camera_led_id ?? '',
      enabled: true
    })) : [];
    
    setFormData(prev => ({
      ...prev,
      raspi_id: newRaspiId,
      chamber_id: chamberId,
      chamber_enabled: chamberEnabled,
      cameras_enabled: cameras_enabled
    }));
  };

  // ============================================================================
  // SECTION 5: SAVE/CLOSE OPERATIONS
  // ============================================================================
  
  // State Variables
  const [isSaving, setIsSaving] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);

  // Helper: Convert formData to experimentData for API
  const formDataToExperimentData = (formData: FormData) => {
    return {
      test_id: formData.test_id,
      profile_id: formData.profile_id,
      profile_file_path: formData.profile_file_path,
      num_profiles: formData.num_profiles,
      start_time: formData.start_time ? new Date(formData.start_time) : null,
      end_time: formData.end_time ? new Date(formData.end_time) : null,
      exp_status_time: null,
      exp_status: 'NEW' as const,
      raspi_id: formData.raspi_id,
      chamber_id: formData.chamber_id,
      chamber_enabled: formData.chamber_enabled,
      cameras_enabled: formData.cameras_enabled,
      exp_alert: 'NO' as const,
      exp_alert_description: '',
      exp_notes: formData.exp_notes,
      exp_time_total: 0,
      exp_time_counter: 0,
      exp_points_total: 0,
      exp_points_counter: 0,
      exp_expect_total: 0,
      exp_expect_counter: 0,
      pressure_steps: formData.pressure_steps,
      temperature_steps: formData.temperature_steps,
      time_steps: formData.time_steps,
      measurement_intervals: formData.measurement_intervals,
      time_interval_steps: [],
    };
  };

  // Comprehensive save logic with camera validation
  const handleSave = async (): Promise<boolean> => {
    // Validate experiment fields and camera configurations before saving
    const validation = validateExperimentSave(formData);
    
    if (!validation.isValid) {
      setSaveError(validation.errors.join(', '));
      return false;
    }
    
    // Clear any previous errors
    setSaveError(null);

    setIsSaving(true);
    try {
      const experimentData = formDataToExperimentData(formData);

      let success = false;

      // Check if this is a new experiment (__NEW__ placeholder)
      if (selectedExpId === "__NEW__") {
        // Create new experiment in database (backend generates exp_id)
        try {
          const createdExperiment = await createExperiment(experimentData);
          if (createdExperiment) {
            success = true;
            // New experiment created successfully
          } else {
            setSaveError('Failed to create experiment in database.');
            return false;
          }
        } catch (error) {
          console.error('Error creating experiment:', error);
          setSaveError('Error creating experiment in database.');
          return false;
        }
      } else if (selectedExpId && allExperiments[selectedExpId]) {
        // Update existing experiment
        success = await updateExperimentFromUser(selectedExpId, experimentData);
      } else {
        setSaveError('Experiment not found. Please refresh and try again.');
        return false;
      }
      
      if (success) {
        setHasUnsavedChanges(false);
        // Refresh experiment data after successful save
        await fetchAllExperimentsRef.current();
        onClose();
        return true;
      } else {
        setSaveError('Failed to save experiment');
        return false;
      }
    } catch (error) {
      console.error('Error saving experiment:', error);
      setSaveError('Error saving experiment');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Handle close with unsaved changes check
  const handleCloseWithCheck = async () => {
    // Check for unsaved changes
    if (hasUnsavedChanges) {
      setShowExitConfirmation(true);
    } else {
      resetFormAndState();
      await fetchAllExperimentsRef.current();
      onClose();
    }
  };

  // Simple exit without save logic
  const handleExitWithoutSave = async () => {
    setShowExitConfirmation(false);
    resetFormAndState();
    await fetchAllExperimentsRef.current();
    onClose();
  };

  // Handle save and exit
  const handleSaveAndExit = async () => {
    const success = await handleSave();
    setShowExitConfirmation(false);
    
    if (success) {
      resetFormAndState();
      await fetchAllExperimentsRef.current();
    }
    // If save failed, don't reset form data - let user see the error and try again
  };

  // Handle save button click with unsaved changes check
  const handleSaveButtonClick = () => {
    if (hasUnsavedChanges) {
      handleSaveAndExit();
    } else {
      onClose(); // Just close without saving if no changes
    }
  };

  // ============================================================================
  // SECTION 6: UTILITY FUNCTIONS
  // ============================================================================
  
  // Get available experiment IDs
  const getAvailableExpIds = (): string[] => {
    const ids = Object.keys(allExperiments).sort();
    // Always include "__NEW__" option at the beginning
    return ["__NEW__", ...ids];
  };

  // Helper: Reset form and all related state
  const resetFormAndState = () => {
    setFormData(getDefaultFormData());
    setSelectedExpId(null);
    setIsEditMode(false);
    setHasUnsavedChanges(false);
    setTestIdWarning(null);
    setProfileFileWarning(null);
    setSaveError(null);
    formDataInitializedRef.current = false;
  };

  // ============================================================================
  // UI COMPUTED VALUES
  // ============================================================================
  
  const displayData = getDisplayData();
  
  // Check if form is disabled (VIEW mode or no exp_id selected)
  const isFormDisabled = !isEditMode || !selectedExpId || (selectedExpId !== "__NEW__" && !allExperiments[selectedExpId]);
  
  // Calculated fields for display
  const validNumProfiles = Number.isFinite(displayData.num_profiles) && displayData.num_profiles > 0 ? displayData.num_profiles : 0;
  const validMeasIntervals = Number.isFinite(displayData.measurement_intervals) && displayData.measurement_intervals > 0 ? displayData.measurement_intervals : 0;
  const validTimeSteps = Array.isArray(displayData.time_steps) && displayData.time_steps.length > 0 && displayData.time_steps.every(t => typeof t === 'number' && !isNaN(t)) ? displayData.time_steps : [];
  const calculatedTotalTime = validNumProfiles > 0 && validTimeSteps.length > 0 ? validNumProfiles * validTimeSteps.reduce((sum, time) => sum + time, 0) : '-';
  const calculatedTotalPoints = validNumProfiles > 0 && validTimeSteps.length > 0 && validMeasIntervals > 0 ? validNumProfiles * validTimeSteps.length * validMeasIntervals : '-';

  // Handle VIEW/EDIT toggle switch
  const handleToggleSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // Switching to EDIT mode
      handleToggleEdit();
    } else {
      // Switching to VIEW mode
      handleToggleView();
    }
  };

  // ============================================================================
  // PROFILE GRAPH HELPERS
  // ============================================================================
  
  // Time unit state: 's' for seconds, 'h' for hours
  const [timeUnit, setTimeUnit] = useState<'s' | 'h'>('s');

  // Helper to generate profile plot data (step line + measurement points)
  // Repeats the profile num_profiles times
  const generateProfilePlotData = ({ steps, values, num_profiles, measurement_intervals }: { steps: number[]; values: number[]; num_profiles: number; measurement_intervals: number }) => {
    // Calculate cycle duration (sum of all time steps)
    const cycleDuration = steps.reduce((sum, step) => sum + step, 0);
    
    // Validate num_profiles (default to 1 if invalid)
    const validNumProfiles = num_profiles > 0 ? num_profiles : 1;
    
    // Validate measurement_intervals (default to 1 if invalid)
    const validMeasurementIntervals = measurement_intervals > 0 ? measurement_intervals : 1;
    
    // Build the step line for all cycles
    const stepLineX = [0];
    const stepLineY = [values[0] ?? null];
    
    // Build measurement points for all cycles
    const measurementX = [];
    const measurementY = [];
    
    // Repeat profile for each cycle
    for (let cycle = 0; cycle < validNumProfiles; cycle++) {
      const cycleOffset = cycle * cycleDuration;
      let time = cycleOffset;
      
      // Build steps for this cycle
      for (let i = 0; i < steps.length; i++) {
        // Add step line point
        time += steps[i];
        stepLineX.push(time);
        stepLineY.push(values[i + 1] ?? values[i]);
        
        // Add measurement points for this step
        for (let j = 0; j < validMeasurementIntervals; j++) {
          const dt = steps[i] * (j / validMeasurementIntervals);
          measurementX.push(time - steps[i] + dt); // Start of step + offset
          measurementY.push(values[i]); // Same value for all points in this step
        }
      }
    }

    return {
      stepLine: { x: stepLineX, y: stepLineY },
      measurement: { x: measurementX, y: measurementY },
    };
  };

  // Helper to convert time array based on unit
  const convertTimeArray = (arr: number[], unit: 's' | 'h') => {
    return unit === 'h' ? arr.map((v) => v / 3600) : arr;
  };

  // Helper to get x-axis title
  const getXAxisTitle = (unit: 's' | 'h') => {
    return unit === 'h' ? 'Time [h]' : 'Time [s]';
  };

  // ============================================================================
  // UI RENDER
  // ============================================================================
  
  return (
    <Dialog 
      open={open} 
      onClose={handleCloseWithCheck}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      {/* Upper Frame */}
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <TextField
          select
          label="EXP ID"
          value={selectedExpId || ''}
          onChange={(e) => {
            const newExpId = e.target.value || null;
            setSelectedExpId(newExpId);
          }}
          disabled={isLoading}
          size="small"
          sx={{ width: 150 }}
        >
          <MenuItem value="">
            <em>Select Experiment</em>
          </MenuItem>
          {getAvailableExpIds().map(expId => (
            <MenuItem key={expId} value={expId}>
              {expId === "__NEW__" ? "NEW" : expId}
            </MenuItem>
          ))}
        </TextField>

        <IconButton
          disabled={isLoading || selectedExpId === "__NEW__"}
          onClick={() => setSelectedExpId("__NEW__")}
          sx={{ color: 'primary.main' }}
        >
          <AddIcon />
        </IconButton>

        <FormControlLabel
          control={
            <Switch
              checked={isEditMode}
              onChange={handleToggleSwitch}
              disabled={isLoading || selectedExpId === "__NEW__" || !selectedExpId || !allExperiments[selectedExpId]}
            />
          }
          label={isEditMode ? "EDIT" : "VIEW"}
          sx={{ ml: 1 }}
        />

        <Box sx={{ flexGrow: 1 }} />

        <IconButton onClick={handleCloseWithCheck}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Error Message */}
      {saveError && (
        <Box sx={{ 
          p: 2, 
          backgroundColor: '#ffebee', 
          borderBottom: '1px solid #f44336',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Typography variant="body2" color="error" sx={{ flexGrow: 1 }}>
            {saveError}
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => setSaveError(null)}
            sx={{ color: 'error.main' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Central Frame */}
      <DialogContent sx={{ p: 2, flexGrow: 1 }}>
        <Grid container spacing={2} sx={{ height: '100%' }}>
          {/* Row 1 */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {/* Row 1, Col 1: Experiment Details */}
              <Grid item xs={4}>
                <Box sx={{ 
                  border: '1px solid #ddd', 
                  borderRadius: 1, 
                  p: 2,
                  height: '100%'
                }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Test ID"
                      value={displayData.test_id === 'N/A' ? '' : (displayData.test_id || '')}
                      placeholder={displayData.test_id === 'N/A' ? 'N/A' : ''}
                      onChange={(e) => handleFieldChange('test_id', e.target.value)}
                      disabled={isFormDisabled}
                      InputProps={{ readOnly: !isEditMode }}
                      fullWidth
                      size="small"
                      error={!!testIdWarning}
                      helperText={testIdWarning}
                    />
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={handleProfileButtonClick}
                        disabled={isFormDisabled}
                        fullWidth
                        sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                      >
                        {displayData.profile_id || 'Select Profile File'}
                      </Button>
                      {displayData.profile_file_path && (
                        <Typography variant="caption" color="text.secondary">
                          File: {displayData.profile_file_path}
                        </Typography>
                      )}
                      {profileFileWarning && (
                        <Typography variant="caption" color="error">
                          {profileFileWarning}
                        </Typography>
                      )}
                    </Box>
                    
                    {/* Calculated Fields Display */}
                    {displayData.profile_id && displayData.time_steps.length > 0 && (
                      <Box sx={{ 
                        mt: 1, 
                        p: 1, 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 1,
                        backgroundColor: '#fafafa'
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                          Calculated Fields
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Exp Total Time [s]: {calculatedTotalTime}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Exp Total Points: {calculatedTotalPoints}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    
                    {/* Hidden file input for profile selection */}
                    <input
                      ref={profileFileInputRef}
                      type="file"
                      accept=".json"
                      onChange={handleProfileFileSelect}
                      style={{ display: 'none' }}
                    />
                    
                    <TextField
                      label="Number of Profiles"
                      type="number"
                      value={displayData.num_profiles}
                      onChange={(e) => handleFieldChange('num_profiles', parseInt(e.target.value) || 0)}
                      disabled={isFormDisabled}
                      InputProps={{ readOnly: !isEditMode }}
                      fullWidth
                      size="small"
                    />
                    
                    <TextField
                      label="Experiment Notes"
                      value={displayData.exp_notes || ''}
                      onChange={(e) => handleFieldChange('exp_notes', e.target.value)}
                      disabled={isFormDisabled}
                      InputProps={{ readOnly: !isEditMode }}
                      fullWidth
                      size="small"
                      multiline
                      rows={3}
                      placeholder="Add any notes or descriptions about this experiment..."
                    />
                  </Box>
                </Box>
              </Grid>

              {/* Row 1, Col 2: Profile Graph */}
              <Grid item xs={8}>
                <Box sx={{ 
                  border: '1px solid #ddd', 
                  borderRadius: 1, 
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  backgroundColor: '#fafafa'
                }}>
                  {displayData.profile_id && displayData.time_steps.length > 0 ? (
                    <>
                      {/* Plots (vertical layout) */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1 }}>
                        {/* Pressure plot */}
                        {(() => {
                          const pressureData = generateProfilePlotData({
                            steps: displayData.time_steps,
                            values: displayData.pressure_steps,
                            num_profiles: displayData.num_profiles,
                            measurement_intervals: displayData.measurement_intervals
                          });
                          return (
                            <Plot
                              data={[
                                // Series 1: Step line
                                {
                                  x: convertTimeArray(pressureData.stepLine.x, timeUnit),
                                  y: pressureData.stepLine.y,
                                  type: 'scatter',
                                  mode: 'lines',
                                  line: { shape: 'hv', color: '#1976d2', width: 2 },
                                  marker: { color: '#1976d2' },
                                  name: `Profile ${displayData.profile_id}`,
                                  showlegend: false,
                                },
                                // Series 2: Measurement points
                                {
                                  x: convertTimeArray(pressureData.measurement.x, timeUnit),
                                  y: pressureData.measurement.y,
                                  type: 'scatter',
                                  mode: 'markers',
                                  marker: { color: 'red', size: 6, symbol: 'circle-open' },
                                  name: 'Measurement Points',
                                  showlegend: false,
                                }
                              ]}
                              layout={{
                                autosize: true,
                                height: 200,
                                margin: { l: 50, r: 20, t: 30, b: 40 },
                                xaxis: {
                                  title: '',
                                  showline: true,
                                  showgrid: true,
                                  zeroline: true,
                                  linecolor: '#333',
                                  linewidth: 2,
                                  mirror: true,
                                },
                                yaxis: {
                                  title: { text: 'Pressure [mmHg]', font: { color: '#222', size: 10 } },
                                  showline: true,
                                  showgrid: true,
                                  zeroline: true,
                                  linecolor: '#333',
                                  linewidth: 2,
                                  mirror: true,
                                },
                                title: 'Pressure Profile',
                                plot_bgcolor: 'white',
                                paper_bgcolor: 'white',
                                showlegend: false,
                              }}
                              useResizeHandler={true}
                              style={{ width: '100%', height: '100%' }}
                              config={{ displayModeBar: false }}
                            />
                          );
                        })()}
                        {/* Temperature plot */}
                        {(() => {
                          const temperatureData = generateProfilePlotData({
                            steps: displayData.time_steps,
                            values: displayData.temperature_steps,
                            num_profiles: displayData.num_profiles,
                            measurement_intervals: displayData.measurement_intervals
                          });
                          return (
                            <Plot
                              data={[
                                // Series 1: Step line
                                {
                                  x: convertTimeArray(temperatureData.stepLine.x, timeUnit),
                                  y: temperatureData.stepLine.y,
                                  type: 'scatter',
                                  mode: 'lines',
                                  line: { shape: 'hv', color: '#1976d2', width: 2 },
                                  marker: { color: '#1976d2' },
                                  name: `Profile ${displayData.profile_id}`,
                                  showlegend: false,
                                },
                                // Series 2: Measurement points
                                {
                                  x: convertTimeArray(temperatureData.measurement.x, timeUnit),
                                  y: temperatureData.measurement.y,
                                  type: 'scatter',
                                  mode: 'markers',
                                  marker: { color: 'red', size: 6, symbol: 'circle-open' },
                                  name: 'Measurement Points',
                                  showlegend: false,
                                }
                              ]}
                              layout={{
                                autosize: true,
                                height: 200,
                                margin: { l: 50, r: 20, t: 30, b: 40 },
                                xaxis: {
                                  title: '',
                                  showline: true,
                                  showgrid: true,
                                  zeroline: true,
                                  linecolor: '#333',
                                  linewidth: 2,
                                  mirror: true,
                                },
                                yaxis: {
                                  title: { text: 'Temperature [°C]', font: { color: '#222', size: 10 } },
                                  showline: true,
                                  showgrid: true,
                                  zeroline: true,
                                  linecolor: '#333',
                                  linewidth: 2,
                                  mirror: true,
                                },
                                title: 'Temperature Profile',
                                plot_bgcolor: 'white',
                                paper_bgcolor: 'white',
                                showlegend: false,
                              }}
                              useResizeHandler={true}
                              style={{ width: '100%', height: '100%' }}
                              config={{ displayModeBar: false }}
                            />
                          );
                        })()}
                      </Box>
                      {/* X-axis title and time unit toggle */}
                      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, borderTop: '1px solid #e0e0e0', pt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {getXAxisTitle(timeUnit)}
                        </Typography>
                        <FormControl component="fieldset" size="small">
                          <RadioGroup
                            row
                            value={timeUnit}
                            onChange={(e) => setTimeUnit(e.target.value as 's' | 'h')}
                            name="time-unit-toggle"
                          >
                            <FormControlLabel value="s" control={<Radio size="small" />} label="[s]" />
                            <FormControlLabel value="h" control={<Radio size="small" />} label="[h]" />
                          </RadioGroup>
                        </FormControl>
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%'
                    }}>
                      <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
                        {displayData.profile_id 
                          ? 'Load a profile file to view the graph'
                          : 'Select a profile file to view the graph'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* Row 2 */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {/* Row 2, Col 1: RASPI and Chamber */}
              <Grid item xs={4}>
                <Box sx={{ 
                  border: '1px solid #ddd', 
                  borderRadius: 1, 
                  p: 2,
                  height: '100%'
                }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      select
                      label="RASPI ID"
                      value={displayData.raspi_id || ''}
                      onChange={(e) => {
                        if (!isEditMode) return;
                        const newRaspiId = e.target.value || null;
                        if (newRaspiId) {
                          updateHardwareForRaspi(newRaspiId);
                        }
                      }}
                      disabled={isFormDisabled}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">
                        <em>Select RASPI</em>
                      </MenuItem>
                      {Object.keys(allRaspis).map(raspiId => (
                        <MenuItem key={raspiId} value={raspiId}>
                          {raspiId}
                        </MenuItem>
                      ))}
                    </TextField>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Checkbox 
                        checked={displayData.chamber_enabled}
                        onChange={(e) => handleFieldChange('chamber_enabled', e.target.checked)}
                        disabled={isFormDisabled || !hasChamberConnected()}
                      />
                      <TextField
                        label="Chamber ID"
                        value={displayData.chamber_id || ''}
                        onChange={(e) => handleFieldChange('chamber_id', e.target.value)}
                        disabled={true}
                        InputProps={{ readOnly: !isEditMode }}
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiInputBase-input': {
                            backgroundColor: '#f5f5f5',
                            color: '#666'
                          }
                        }}
                      />
                    </Box>
                    
                    {/* Chamber status indicator */}
                    {displayData.raspi_id && (
                      <Box sx={{ 
                        mt: 1, 
                        p: 1, 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 1,
                        backgroundColor: '#fafafa'
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                          Chamber Status
                        </Typography>
                        {(() => {
                          const { hasChamber, chamberId, isConnected } = getChamberStatus();
                          if (!hasChamber) {
                            return (
                              <Typography variant="caption" color="error">
                                No chamber connected to this RASPI
                              </Typography>
                            );
                          } else if (!isConnected) {
                            return (
                              <Typography variant="caption" color="warning.main">
                                RASPI is disconnected - chamber unavailable
                              </Typography>
                            );
                          } else {
                            return (
                              <Typography variant="caption" color="success.main">
                                Chamber {chamberId} is connected and available
                              </Typography>
                            );
                          }
                        })()}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Row 2, Col 2: Camera Configuration */}
              <Grid item xs={8}>
            <Box sx={{ 
              border: '1px solid #ddd', 
              borderRadius: 1, 
              p: 2,
              height: '100%',
              overflow: 'auto'
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {getSelectedRaspiCameras().map((camera) => (
                  <Box key={camera.id} sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1, 
                    p: 1.5,
                    backgroundColor: '#fafafa'
                  }}>
                    <Checkbox 
                      checked={displayData.cameras_enabled.some(c => c.camera_id === camera.id && c.enabled)}
                      onChange={(e) => {
                        if (!isEditMode) return; // Don't allow changes in VIEW mode
                        
                        if (e.target.checked) {
                          // Add camera to enabled list
                          setFormData(prev => ({
                            ...prev,
                            cameras_enabled: [...prev.cameras_enabled, {
                              camera_id: camera.id,
                              camera_gauge_id: camera.data?.camera_gauge_id ?? '',
                              camera_led_id: camera.data?.camera_led_id ?? '',
                              enabled: true
                            }]
                          }));
                        } else {
                          // Remove camera from enabled list
                          setFormData(prev => ({
                            ...prev,
                            cameras_enabled: prev.cameras_enabled.filter(c => c.camera_id !== camera.id)
                          }));
                        }
                        setHasUnsavedChanges(true);
                      }}
                      disabled={isFormDisabled || !hasCamerasConnected()}
                    />
                    
                    <TextField
                      label="Camera ID"
                      value={camera.id}
                      disabled={true} // Read-only
                      size="small"
                      sx={{
                        minWidth: 120,
                        '& .MuiInputBase-input': {
                          backgroundColor: '#f5f5f5',
                          color: '#666'
                        }
                      }}
                    />
                    
                    <TextField
                      label="Gauge ID"
                      value={camera.data?.camera_gauge_id ?? ''}
                      disabled={true} // Read-only display only
                      size="small"
                      sx={{
                        minWidth: 120,
                        '& .MuiInputBase-input': {
                          backgroundColor: '#f5f5f5',
                          color: '#666'
                        }
                      }}
                    />
                    
                    <TextField
                      label="LED ID"
                      value={camera.data?.camera_led_id ?? ''}
                      disabled={true} // Read-only display only
                      size="small"
                      sx={{
                        minWidth: 120,
                        '& .MuiInputBase-input': {
                          backgroundColor: '#f5f5f5',
                          color: '#666'
                        }
                      }}
                    />
                  </Box>
                ))}
                
                {getSelectedRaspiCameras().length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    {displayData.raspi_id ? 'No cameras found for selected RASPI' : 'Select a RASPI to view cameras'}
                  </Typography>
                )}
                
                {/* Camera status indicator */}
                {displayData.raspi_id && (
                  <Box sx={{ 
                    mt: 1, 
                    p: 1, 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1,
                    backgroundColor: '#fafafa'
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                      Camera Status
                    </Typography>
                    {(() => {
                      const { hasCameras, cameraCount, connectedCount } = getCameraStatus();
                      if (!hasCameras) {
                        return (
                          <Typography variant="caption" color="error">
                            No cameras connected to this RASPI
                          </Typography>
                        );
                      } else if (connectedCount === 0) {
                        return (
                          <Typography variant="caption" color="warning.main">
                            RASPI is disconnected - cameras unavailable
                          </Typography>
                        );
                      } else if (connectedCount < cameraCount) {
                        return (
                          <Typography variant="caption" color="warning.main">
                            {connectedCount} of {cameraCount} camera(s) available
                          </Typography>
                        );
                      } else {
                        return (
                          <Typography variant="caption" color="success.main">
                            All {cameraCount} camera(s) connected and available
                          </Typography>
                        );
                      }
                    })()}
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      {/* Lower Frame */}
      <Box sx={{ 
        p: 2, 
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <Button
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSaveButtonClick}
          disabled={!isEditMode || isSaving}
        >
          SAVE
        </Button>
      </Box>

      {/* Exit Confirmation Dialog */}
      <Dialog
        open={showExitConfirmation}
        onClose={() => setShowExitConfirmation(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Do you want to save before exiting?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExitWithoutSave} color="error">
            Exit Without Save
          </Button>
          <Button onClick={() => setShowExitConfirmation(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveAndExit} variant="contained" autoFocus>
            Save and Exit
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ExperimentEditDialog;
